import  { useState, useEffect } from "react";
import axios from "axios";
import loading from "../../assests/loading .gif";
import nologs from "../../assests/nologs.gif";
import CustomTooltip from "../Buttons/CustomTooltip";

function LogModels({
  logshow,
  logsetShow,
  jobId,
  fileId,
  jobName,
  fileName,
  moduleType = "migration", // Default to migration, can pass "sourceProfiler"
}) {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fileProcessCompleted, setFileProcessCompleted] = useState(false);
  const [jobProcessCompleted, setJobProcessCompleted] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const handlelogClose = () => logsetShow(false);

  const truncateText = (text, maxLength) => {
    if (!text) return "N/A";

    const isTruncated = text.length > maxLength;
    const truncated = isTruncated ? `${text.substring(0, maxLength)}...` : text;

    return isTruncated ? (
      <CustomTooltip
        content={text}
        placement="right"
        fallbackPlacements={["top"]}
      >
        <span className="truncated-text" style={{ cursor: "pointer" }}>
          {truncated}
        </span>
      </CustomTooltip>
    ) : (
      <span>{text}</span>
    );
  };

  // Effect for Migration module
  useEffect(() => {
    if (moduleType !== "migration" || !jobId) return;

    setLogs([]);
    setIsLoading(true);
    setFileProcessCompleted(false);
    setJobProcessCompleted(false);
    setIsStreaming(false);

    const token = sessionStorage.getItem("access_token");
    if (!token) {
      console.error("Token missing");
      return;
    }

    const ws = new WebSocket(
      `${process.env.REACT_APP_API_URL_CODE_CONVERSION_WEB_SOCKET}/ws/logs/${jobId}`
    );

    ws.onopen = () => {
      ws.send(JSON.stringify({ token, file_id: fileId }));
    };

    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      const { event: type, data } = msg;

      if (msg.error) {
        console.error("WebSocket error:", msg.error);
        ws.close();
        return;
      }

      if (type === "full_update") {
        setLogs(data);
        setIsLoading(false);
        setIsStreaming(true);
        checkForCompletion(data);
      }

      if (type === "incremental_update") {
        setLogs((prevLogs) => {
          const updatedLogs = [...prevLogs, ...data];
          checkForCompletion(updatedLogs);
          return updatedLogs;
        });
        setIsLoading(false);
        setIsStreaming(true);
      }
    };

    ws.onerror = (err) => {
      console.error("WebSocket error:", err);
      setIsStreaming(false);
    };

    ws.onclose = () => {
      console.log("WebSocket closed");
      setIsStreaming(false);
    };

    const checkForCompletion = (logEntries) => {
      if (fileId) {
        const hasFileCompletion = logEntries.some(
          (log) =>
            log.message.toLowerCase().includes("process completed for file") ||
            log.message.toLowerCase().includes("process failed for file")
        );
        if (hasFileCompletion) {
          setFileProcessCompleted(true);
          ws.close();
        }
      } else {
        const hasJobCompletion = logEntries.some(
          (log) =>
            (log.message &&
              log.message
                .toLowerCase()
                .includes("process completed for job")) ||
            log.message.toLowerCase().includes("process failed for job")
        );
        if (hasJobCompletion) {
          setJobProcessCompleted(true);
          ws.close();
        }
      }
    };

    return () => {
      ws.close();
      setIsStreaming(false);
    };
  }, [jobId, fileId, moduleType]);

  // Effect for Source Profiler module
  useEffect(() => {
    // Only run hook for migration jobs with valid jobId
    if (moduleType !== "sourceProfiler" || !jobId) return;

    setLogs([]);
    setIsLoading(true);
    setJobProcessCompleted(false);
    setIsStreaming(false);

    const token = sessionStorage.getItem("access_token");
    if (!token) {
      console.error("Token missing");
      setIsLoading(false);
      return;
    }

    // Compose WebSocket URL
    const ws = new WebSocket(
      `${process.env.REACT_APP_API_URL_CODE_SOURCE_PROFILER_WEB_SOCKET}/ws/logs/${jobId}`
    );

    ws.onopen = () => {
      // Send ONLY the token, per new API (no file_id!)
      ws.send(JSON.stringify({ token }));
    };

    ws.onmessage = (event) => {
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch (e) {
        console.error("WebSocket: Failed to parse message", e);
        return;
      }

      const { event: type, data, error } = msg;

      // Handle potential auth/access errors
      if (error) {
        console.error("WebSocket error:", error);
        ws.close();
        setIsStreaming(false);
        setIsLoading(false);
        return;
      }

      if (type === "full_update") {
        setLogs(data);
        setIsLoading(false);
        setIsStreaming(true);
        checkForJobCompletion(data);
      } else if (type === "incremental_update") {
        setLogs((prevLogs) => {
          const updatedLogs = [...prevLogs, ...data];
          checkForJobCompletion(updatedLogs);
          return updatedLogs;
        });
        setIsLoading(false);
        setIsStreaming(true);
      }
    };

    ws.onerror = (err) => {
      console.error("WebSocket error:", err);
      setIsStreaming(false);
      setIsLoading(false);
    };

    ws.onclose = () => {
      console.log("WebSocket closed");
      setIsStreaming(false);
    };

    // Job-level completion check only, no per-file logic
    const checkForJobCompletion = (logEntries) => {
      const hasJobCompletion = logEntries.some(
        (log) =>
          log.message &&
          (log.message.toLowerCase().includes("process completed for job") ||
            log.message.toLowerCase().includes("process failed for job"))
      );
      if (hasJobCompletion) {
        setJobProcessCompleted(true);
        ws.close();
      }
    };

    return () => {
      ws.close();
      setIsStreaming(false);
    };
    // fileId dependency removed, as it is no longer used
  }, [jobId, moduleType]);

  // Determine completion status and message based on module type
  const processCompleted =
    moduleType === "sourceProfiler"
      ? jobProcessCompleted
      : fileId
      ? fileProcessCompleted
      : jobProcessCompleted;

  const completionMessage =
    moduleType === "sourceProfiler"
      ? "Job processing completed! ✅"
      : fileId
      ? "File processing completed! ✅"
      : "Job processing completed! ✅";

  const inProgressMessage =
    moduleType === "sourceProfiler"
      ? "Job scanning in progress...🛠️"
      : fileId
      ? "File conversion in progress...🛠️"
      : "Job conversion in progress...🛠️";

  const descriptionMessage = processCompleted
    ? moduleType === "sourceProfiler"
      ? "Job scanning has been completed successfully!"
      : fileId
      ? "File processing has been completed successfully!"
      : "All files in this job have been processed successfully!"
    : "Your code is getting migrated to the target. Sit tight, and we'll notify you once it's done.";


  // Rest of the component remains the same...
  const logContainerStyle =
    (logs?.length || 0) > 0
      ? { height: "40vh", overflowY: "auto" }
      : {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "40vh",
        };

  const highlightKeywords = (text) => {
    if (!text) return text;
    const greenKeywords = ["completed", "successfully"];
    const yellowKeywords = [
      "SQL",
      "T-SQL",
      "output",
      "progress",
      "Transforming",
      "Converting",
      "folder",
      "file",
    ];
    const redKeywords = ["of", "table", "queries", "Transformimg"];
    const newWords = ["Started", "database", "data"];
    const errorWords = ["error", "failed"];

    const regex = new RegExp(
      `\\b(${[
        ...greenKeywords,
        ...yellowKeywords,
        ...redKeywords,
        ...newWords,
        ...errorWords,
      ].join("|")})\\b`,
      "gi"
    );

    return text.split(regex).map((part, index) => {
      if (
        greenKeywords.some((word) => word.toLowerCase() === part.toLowerCase())
      ) {
        return (
          <span key={index} style={{ color: "#57FFEF", fontWeight: "bold" }}>
            {part}
          </span>
        );
      } else if (
        yellowKeywords.some((word) => word.toLowerCase() === part.toLowerCase())
      ) {
        return (
          <span key={index} style={{ color: "#FFD03C", fontWeight: "bold" }}>
            {part}
          </span>
        );
      } else if (
        redKeywords.some((word) => word.toLowerCase() === part.toLowerCase())
      ) {
        return (
          <span key={index} style={{ color: "#F97583", fontWeight: "bold" }}>
            {part}
          </span>
        );
      } else if (
        newWords.some((word) => word.toLowerCase() === part.toLowerCase())
      ) {
        return (
          <span key={index} style={{ color: "#B392F0", fontWeight: "bold" }}>
            {part}
          </span>
        );
      } else if (
        errorWords.some((word) => word.toLowerCase() === part.toLowerCase())
      ) {
        return (
          <span key={index} style={{ color: "#ec9395", fontWeight: "bold" }}>
            {part}
          </span>
        );
      }

      return part;
    });
  };

  return (
    <div className={`right-bar2 p-3 ${logshow ? "d-block" : "d-none"}`}>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 0.6; }
            50% { opacity: 0.3; }
            100% { opacity: 0.6; }
          }
        `}
      </style>
      <div data-simplebar className="h-100">
        <div className="d-flex align-item-center justify-content-between">
          <p className=" fw-medium text-dark fw-bold">Logs</p>
          <CustomTooltip
            content="Close"
            placement="left"
            fallbackPlacements={["bottom"]}
          >
            <i
              onClick={handlelogClose}
              className="mdi mdi-close close-trace cursor-pointer right-bar-toggle3 iconColor"
            />
          </CustomTooltip>
        </div>
        <div
          className="d-flex align-item-center justify-content-between "
          style={{ marginTop: "-4px" }}
        >
          <div>
            <h6 className="mb-0 mt-0">
              Logs for{" "}
              {moduleType === "sourceProfiler"
                ? "Job"
                : fileId
                ? "File"
                : "Job"}
              :{" "}
              <span>
                {moduleType === "sourceProfiler"
                  ? truncateText(jobName, 15) || "N/A"
                  : fileId
                  ? truncateText(fileName, 18)
                  : truncateText(jobName, 15) || "N/A"}
              </span>{" "}
              {processCompleted ? completionMessage : inProgressMessage}
            </h6>
            <p className="font-size-11 mt-2 mb-3">{descriptionMessage}</p>
          </div>
        </div>

        <div className="p-3 card-calc" style={logContainerStyle}>
          {isLoading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "200px" }}
            >
              <img
                src={loading}
                alt="Loading..."
                style={{ width: "20%", height: "auto" }}
              />
            </div>
          ) : logs?.length > 0 ? (
            <div
              className="log-container"
              style={{ fontSize: "14px", fontFamily: "Helvetica" }}
            >
              <div
                style={{
                  display: "flex",
                  paddingBottom: "8px",
                }}
              >
                {moduleType !== "sourceProfiler" && (
                  <div
                    style={{
                      width: "23%",
                      color: "white",
                      paddingRight: "16px",
                    }}
                  >
                    Timestamp
                  </div>
                )}

                {moduleType == "sourceProfiler" && (
                  <div
                    style={{
                      width: "45%",
                      color: "white",
                      paddingRight: "16px",
                    }}
                  >
                    Timestamp
                  </div>
                )}

                {moduleType === "sourceProfiler" ? (
                  <div
                    style={{
                      width: "26%",
                      color: "white",
                      paddingRight: "16px",
                      paddingLeft: "0px",
                    }}
                  >
                    Log Level
                  </div> // Source Profiler shows log_level
                ) : (
                  <div
                    style={{
                      width: "26%",
                      color: "white",
                      paddingRight: "16px",
                      paddingLeft: "0px",
                    }}
                  >
                    File Name
                  </div> // Migration shows file_name
                )}
                <div
                  style={{
                    width: moduleType === "sourceProfiler" ? "77%" : "52%",
                    color: "white",
                    paddingLeft:
                      moduleType === "sourceProfiler" ? "0px" : "16px",
                  }}
                >
                  Summary
                </div>
              </div>

              {logs.map((log, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    padding: "6px 0",
                    fontSize: "12px",
                    fontFamily: "'JetBrains Mono', monospace",
                    minHeight: "40px",
                    alignItems: "flex-start",
                  }}
                >
                  {moduleType !== "sourceProfiler" && (
                    <div
                      style={{
                        width: "23%",
                        color: "white",
                        paddingRight: "16px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {log.timestamp}
                    </div>
                  )}
                  {moduleType == "sourceProfiler" && (
                    <div
                      style={{
                        width: "45%",
                        color: "white",
                        paddingRight: "16px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {log.timestamp}
                    </div>
                  )}
                  {moduleType !== "sourceProfiler" && (
                    <div
                      style={{
                        width: "26%",
                        color: "white",
                        paddingRight: "16px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                      }}
                    >
                      {log.file_name || "N/A"}
                    </div>
                  )}
                  {moduleType === "sourceProfiler" && (
                    <div
                      style={{
                        width: "26%",
                        color: "white",
                        paddingRight: "16px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                        color:
                          log.log_level === "ERROR"
                            ? "#ec9395"
                            : log.log_level === "WARN"
                            ? "#FFD03C"
                            : log.log_level === "INFO"
                            ? "#57FFEF"
                            : "white",
                      }}
                    >
                      {log.log_level}
                    </div>
                  )}
                  <div
                    style={{
                      width: moduleType === "sourceProfiler" ? "77%" : "52%",
                      color: "white",
                      paddingLeft:
                        moduleType === "sourceProfiler" ? "0px" : "16px",
                      wordBreak: "break-word",
                    }}
                  >
                    {highlightKeywords(log.message)}
                  </div>
                </div>
              ))}

              {isStreaming && !processCompleted && (
                <div
                  style={{
                    display: "flex",
                    padding: "6px 0",
                    fontSize: "12px",
                    fontFamily: "'JetBrains Mono', monospace",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      width: "23%",
                      paddingRight: "16px",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        height: "14px",
                        backgroundColor: "rgb(160 168 181 / 31%)",
                        borderRadius: "2px",
                        animation: "pulse 1.5s infinite",
                      }}
                    ></div>
                  </div>

                  {moduleType !== "sourceProfiler" && (
                    <div
                      style={{
                        width: "26%",
                        paddingRight: "16px",
                      }}
                    >
                      <div
                        style={{
                          width: "100%",
                          height: "14px",
                          backgroundColor: "rgb(160 168 181 / 31%)",
                          borderRadius: "2px",
                          animation: "pulse 1.5s infinite",
                        }}
                      ></div>
                    </div>
                  )}

                  <div
                    style={{
                      width: moduleType === "sourceProfiler" ? "77%" : "52%",
                      paddingLeft:
                        moduleType === "sourceProfiler" ? "0px" : "16px",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        height: "14px",
                        backgroundColor: "rgb(160 168 181 / 31%)",
                        borderRadius: "2px",
                        animation: "pulse 1.5s infinite",
                      }}
                    ></div>
                    <div
                      style={{
                        width: "100%",
                        height: "14px",
                        backgroundColor: "rgb(160 168 181 / 31%)",
                        borderRadius: "2px",
                        marginTop: "4px",
                        animation: "pulse 1.5s infinite",
                        animationDelay: "0.3s",
                      }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div
              className="d-flex flex-column justify-content-center align-items-center"
              style={{ height: "200px" }}
            >
              <img
                src={nologs}
                alt="No Logs..."
                style={{ width: "30%", height: "auto" }}
              />
              <p>No Logs Available</p>
            </div>
          )}
        </div>

        <div
          className="position-absolute w-100 bottom-2px"
          style={{ paddingBottom: "0px" }}
        >
          <div className="d-flex justify-content-between">
            <button
              onClick={handlelogClose}
              className="btn btn-closebtn custom-button"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LogModels;
